import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { v4 as uuidv4 } from 'uuid'; // Pour générer des ID uniques

const SnackbarContext = createContext(null);

export const SnackbarProvider = ({ children }) => {
    const [snackPacks, setSnackPacks] = useState([]);
    const snackRefs = useRef({});
    const [renderedHeights, setRenderedHeights] = useState({});

    const showMessage = useCallback((msg, severityLevel = 'info', duration = 5000) => {
        const id = uuidv4();
        const newSnack = { id, msg, severityLevel, duration, open: true };
        setSnackPacks((prev) => [...prev, newSnack]);
        snackRefs.current[id] = React.createRef();
    }, []);

    const handleClose = useCallback((event, reason, id) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackPacks((prev) => prev.map(snack => snack.id === id ? { ...snack, open: false } : snack));
    }, []);

    useEffect(() => {
        const updateHeights = () => {
            const heights = {};
            snackPacks.forEach(snack => {
                const ref = snackRefs.current[snack.id];
                if (ref && ref.current) {
                    heights[snack.id] = ref.current.clientHeight;
                }
            });
            setRenderedHeights(heights);
        };

        updateHeights();
    }, [snackPacks]);

    const calculateTopOffset = (id) => {
        let offset = 20;
        let found = false;
        for (let i = 0; i < snackPacks.length; i++) {
            const snack = snackPacks[i];
            if (snack.id === id) {
                found = true;
                continue;
            }
            if (found && renderedHeights[snack.id]) {
                offset += renderedHeights[snack.id] + 10;
            }
        }
        return offset;
    };

    const renderMessage = (msg) => {
        if (Array.isArray(msg)) {
            return msg.map((m, index) => <div key={index}>{m}</div>);
        } else {
            return msg;
        }
    }

    return (
        <SnackbarContext.Provider value={{ showMessage }}>
            {children}
            {snackPacks.map(({ id, msg, severityLevel, duration, open }) => (
                <Snackbar
                    key={id}
                    ref={snackRefs.current[id]}
                    open={open}
                    autoHideDuration={duration}
                    onClose={(event, reason) => handleClose(event, reason, id)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    style={{ top: `${calculateTopOffset(id)}px`, transition: 'top 300ms ease' }}
                >
                    <Alert onClose={(event, reason) => handleClose(event, reason, id)}
                        severity={severityLevel} sx={{ width: '100%' }} variant="filled">
                        {renderMessage(msg)}
                    </Alert>
                </Snackbar>
            ))}
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
